/**
 * `/login` (#371, D30): username and password.
 *
 * Outside the app shell and outside any data dependency - it must render for a
 * tab that has no session, which is the only kind of tab that reaches it.
 *
 * What this page does and does not know:
 *
 * - **One failure sentence for every failed sign-in.** The server answers every
 *   failure (unknown name, wrong password, disabled account, a locked-out name)
 *   with `invalid_credentials`, and this page says the same thing for all of
 *   them. See `i18n/auth-labels.ts`.
 * - **The password is cleared on failure.** A mistyped password is better
 *   retyped than left in a field, and a cleared field is also what stops a
 *   password sitting in the DOM after a refusal.
 * - **A successful sign-in clears the query cache.** Nothing fetched under a
 *   previous session, or a previous account on this browser, may still be
 *   showing for whoever is now signed in. (Sign-out and a 401 redirect also
 *   discard it - the latter by a hard navigation.)
 * - **Nothing is remembered.** No username in storage, no "remember me": §6.2
 *   keeps everything about the learner out of browser storage, and the browser's
 *   own password manager (the `autocomplete` hints below) is the right place for
 *   a credential.
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import type { LoginRequest } from '@davinci/contracts';
import { ApiRequestError } from '../../api/client.js';
import { useApi } from '../../app/api-context.js';
import { AuthStage } from '../../app/auth-layout.js';
import { FailureNotice } from '../../app/failure-notice.js';
import { authMessages, signInFailureSentence } from '../../i18n/auth-labels.js';
import { useMessages } from '../../i18n/locale.js';
import { defineMessages } from '../../i18n/messages.js';

const messages = defineMessages({
  en: {
    title: 'Sign in',
    errorHeading: 'DaVinci could not sign you in',
    username: 'Username',
    password: 'Password',
    submitting: 'Signing in…',
    submit: 'Sign in',
    haveInvite: 'Have an invite code?',
    createAccount: 'Create an account',
  },
  he: {
    title: 'התחברות',
    errorHeading: 'DaVinci לא הצליח לחבר אותך',
    username: 'שם משתמש',
    password: 'סיסמה',
    submitting: 'מתחבר…',
    submit: 'התחברות',
    haveInvite: 'יש לך קוד הזמנה?',
    createAccount: 'יצירת חשבון',
  },
});

export function LoginPage(): React.JSX.Element {
  const t = useMessages(messages);
  const a = useMessages(authMessages);
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [missing, setMissing] = useState(false);
  const summaryRef = useRef<HTMLDivElement>(null);

  const mutation = useMutation({
    mutationFn: (body: LoginRequest) => api.login(body),
    onSuccess: () => {
      queryClient.clear();
      void navigate('/', { replace: true });
    },
    onError: () => setPassword(''),
  });

  const sentence =
    mutation.error instanceof ApiRequestError
      ? signInFailureSentence(mutation.error.code, a)
      : undefined;
  const showSummary = missing || sentence !== undefined;

  useEffect(() => {
    // Only after a submission the person just made - never on first render.
    if (showSummary) summaryRef.current?.focus();
  }, [showSummary, mutation.submittedAt]);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (username.trim() === '' || password === '') {
      setMissing(true);
      return;
    }
    setMissing(false);
    mutation.mutate({ username: username.trim(), password });
  }

  return (
    <AuthStage compact glyph={'🔑'}>
      <h1>{t.title}</h1>

      {showSummary ? (
        <div className="notice notice-error" role="alert" tabIndex={-1} ref={summaryRef}>
          <h2>{t.errorHeading}</h2>
          <p>{missing ? a.signInValidation : sentence}</p>
        </div>
      ) : mutation.isError ? (
        // A network failure or an unexpected refusal: the shared notice, which
        // never renders anything the server wrote.
        <FailureNotice error={mutation.error} />
      ) : null}

      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <div className="field">
          <label htmlFor="login-username">{t.username}</label>
          <input
            id="login-username"
            name="username"
            type="text"
            dir="ltr"
            required
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="login-password">{t.password}</label>
          <input
            id="login-password"
            name="password"
            type="password"
            dir="ltr"
            required
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>

        <button className="auth-submit" type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? t.submitting : t.submit}
        </button>
      </form>

      <p className="auth-foot">
        {t.haveInvite} <Link to="/signup">{t.createAccount}</Link>
      </p>
    </AuthStage>
  );
}
